import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  UserPlus,
  Mail,
  CheckCircle,
  ArrowRight,
  Bell,
  Lightbulb,
  Award,
  Briefcase,
  GraduationCap,
  Building,
  Target,
} from 'lucide-react';
import QoreTalksShell from './QoreTalksShell';
import QtAccordion from './QtAccordion';
import { submitGuestApplication } from '../../services/qoreTalksApi';

const PROFILES = [
  { icon: Briefcase, key: 'industry' },
  { icon: Lightbulb, key: 'innovators' },
  { icon: GraduationCap, key: 'research' },
  { icon: Building, key: 'manufacturing' },
  { icon: Award, key: 'mentors' },
  { icon: Target, key: 'specialists' },
];

const TOPICS = [
  'ai', 'manufacturing', 'career', 'startup', 'sustainable', 'hardware',
  'leadership', 'industry40', 'research', 'education',
];

const STEPS = ['prep', 'recording', 'production', 'promotion'];

const GuestApply = () => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [topic, setTopic] = useState('');
  const [message, setMessage] = useState('');
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [expertiseArea, setExpertiseArea] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !message || !name || !topic) return;
    setIsLoading(true);
    try {
      await submitGuestApplication({
        name,
        email,
        topic,
        message,
        linkedinUrl: linkedinUrl || undefined,
        expertiseArea: expertiseArea || undefined,
      });
      setIsSubmitted(true);
      setName('');
      setEmail('');
      setTopic('');
      setMessage('');
      setLinkedinUrl('');
      setExpertiseArea('');
    } catch (error) {
      alert(error.message || 'Failed to submit');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <QoreTalksShell
      activeTab="apply"
      hero={{
        badge: t('qoreTalks.applyTitle'),
        title: t('qoreTalks.pageTitle'),
        titleAccent: t('qoreTalks.applyToSpeak'),
        subtitle: t('qoreTalks.pageSubtitle'),
      }}
      mainClass="qt-main--wide"
    >
      <section className="qt-section" id="qt-apply-form" style={{ paddingTop: 0 }}>
        <h2 className="qt-section__title">{t('qoreTalks.applyTitle')}</h2>
        <p className="qt-section__subtitle">{t('qoreTalks.applyDesc')}</p>
        {!isSubmitted ? (
          <form onSubmit={handleSubmit} className="qt-form" style={{ maxWidth: 560 }}>
            <input className="qt-input" placeholder={t('qoreTalks.applyName')} value={name} onChange={(e) => setName(e.target.value)} required />
            <input className="qt-input" type="email" placeholder={t('qoreTalks.applyEmail')} value={email} onChange={(e) => setEmail(e.target.value)} required />
            <input className="qt-input" placeholder={t('qoreTalks.applyTopic')} value={topic} onChange={(e) => setTopic(e.target.value)} required />
            <input className="qt-input" type="url" placeholder={t('qoreTalks.applyLinkedin')} value={linkedinUrl} onChange={(e) => setLinkedinUrl(e.target.value)} />
            <input className="qt-input" placeholder={t('qoreTalks.applyExpertise')} value={expertiseArea} onChange={(e) => setExpertiseArea(e.target.value)} />
            <textarea className="qt-textarea" placeholder={t('qoreTalks.applyMessage')} value={message} onChange={(e) => setMessage(e.target.value)} required />
            <button type="submit" className="qt-btn qt-btn--primary qt-btn--lg" disabled={isLoading} style={{ width: '100%' }}>
              <Mail size={18} />
              {isLoading ? t('qoreTalks.applySubmitting') : t('qoreTalks.applySubmit')}
              <ArrowRight size={18} />
            </button>
          </form>
        ) : (
          <div className="qt-success" style={{ maxWidth: 560, margin: '0 auto' }}>
            <CheckCircle size={36} color="#34d399" />
            <h3>{t('qoreTalks.applySuccessTitle')}</h3>
            <p>{t('qoreTalks.applySuccessDesc')}</p>
          </div>
        )}
        <p style={{ textAlign: 'center', marginTop: '1rem', fontSize: '0.875rem', color: '#64748b' }}>
          <a href="mailto:podcast@qorenova.com" style={{ color: '#a5b4fc' }}>podcast@qorenova.com</a>
        </p>
      </section>

      <QtAccordion title={t('qoreTalks.accordionWho')}>
        <div className="qt-feature-grid qt-feature-grid--3">
          {PROFILES.map(({ icon: Icon, key }) => (
            <div className="qt-feature-card" key={key}>
              <div className="qt-feature-card__icon">
                <Icon size={24} />
              </div>
              <h4>{t(`qoreTalks.profiles.${key}.title`)}</h4>
              <p>{t(`qoreTalks.profiles.${key}.desc`)}</p>
            </div>
          ))}
        </div>
      </QtAccordion>

      <QtAccordion title={t('qoreTalks.accordionTopics')}>
        <div className="qt-tags">
          {TOPICS.map((key) => (
            <span className="qt-tag" key={key}>
              <CheckCircle size={14} /> {t(`qoreTalks.topics.${key}`)}
            </span>
          ))}
        </div>
      </QtAccordion>

      <QtAccordion title={t('qoreTalks.accordionExpect')}>
        <div className="qt-feature-grid">
          {STEPS.map((key, i) => (
            <div className="qt-feature-card" key={key} style={{ textAlign: 'left' }}>
              <div className="qt-expect-num">{String(i + 1).padStart(2, '0')}</div>
              <h4>{t(`qoreTalks.expect.${key}.title`)}</h4>
              <p>{t(`qoreTalks.expect.${key}.desc`)}</p>
            </div>
          ))}
        </div>
      </QtAccordion>

      <div className="qt-cta-panel" style={{ marginTop: '2rem' }}>
        <Bell size={36} style={{ color: '#a5b4fc' }} />
        <h3>{t('qoreTalks.nominateTitle')}</h3>
        <p>{t('qoreTalks.nominateDesc')}</p>
        <a href="#qt-apply-form" className="qt-btn qt-btn--primary">
          <UserPlus size={18} /> {t('qoreTalks.nominateBtn')}
        </a>
      </div>
    </QoreTalksShell>
  );
};

export default GuestApply;